'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useGclid } from '@/hooks/useGclid';

interface QuoteFormProps {
  className?: string;
}

interface FormData {
  contactName: string;
  companyName: string;
  email: string;
  phone: string;
  message: string;
}

interface FieldErrors {
  contactName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  message?: string;
}

const QuoteForm = ({ className = '' }: QuoteFormProps) => {
  const { gclid } = useGclid();
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    contactName: '',
    companyName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'contactName':
        return '';
      
      case 'companyName':
        return '';
      
      case 'email':
        if (!value.trim()) {
          return 'Email address is required';
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Please enter a valid email address (e.g., john@company.com)';
        }
        return '';
      
      case 'phone':
        if (!value.trim()) {
          return 'WhatsApp number is required';
        }
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length < 10) {
          return 'Please enter a valid phone number with at least 10 digits';
        }
        return '';
      
      case 'message':
        if (!value.trim()) {
          return 'Project description is required';
        }
        return '';
      
      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    // Real-time validation for touched fields
    if (touchedFields.has(name)) {
      const error = validateField(name, value);
      setFieldErrors({
        ...fieldErrors,
        [name]: error
      });
    }

    // Clear submit error when user starts typing
    if (submitError) {
      setSubmitError('');
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Mark field as touched
    setTouchedFields(new Set(touchedFields).add(name));

    // Validate on blur
    const error = validateField(name, value);
    setFieldErrors({
      ...fieldErrors,
      [name]: error
    });
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Validate all fields
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key as keyof FormData]);
      if (error) {
        errors[key as keyof FieldErrors] = error;
        isValid = false;
      }
    });

    setFieldErrors(errors);
    
    // Mark all fields as touched
    setTouchedFields(new Set(Object.keys(formData)));
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields before submission
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Missing Supabase configuration. Please check environment variables.');
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/send-quote-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'apikey': supabaseAnonKey
          },
          body: JSON.stringify({ ...formData, gclid })
        }
      );

      let data: { success?: boolean; error?: string; message?: string; emailId?: string };
      try {
        data = await response.json();
      } catch {
        throw new Error(`Server returned status ${response.status} with non-JSON response`);
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed with status ${response.status}`);
      }

      setIsSubmitting(false);
      setShowSuccess(true);
      setFormData({
        contactName: '',
        companyName: '',
        email: '',
        phone: '',
        message: ''
      });
      setTouchedFields(new Set());
      setFieldErrors({});

      // Redirect to thank you page after 1 second
      setTimeout(() => {
        window.location.href = 'https://wiliang.com/thank-you';
      }, 1000);
    } catch (error) {
      setIsSubmitting(false);
      const errMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      if (errMsg.includes('Failed to fetch') || errMsg.includes('NetworkError') || errMsg.includes('fetch')) {
        setSubmitError('Cannot connect to email service. Please check your internet connection and try again.');
      } else {
        setSubmitError(errMsg);
      }
    }
  };

  if (!isHydrated) {
    return (
      <div className={`max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 ${className}`}>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          <div className="space-y-4">
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className={`max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 ${className}`}>
        <div className="text-center py-12 animate-fadeIn">
          <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
          </div>
          <h3 className="font-headline font-bold text-3xl text-primary mb-4">
            Quote Request Received!
          </h3>
          <p className="font-body text-lg text-text-secondary mb-6 max-w-md mx-auto">
            Thank you for your interest. Our team will review your requirements and respond within 12 hours with a detailed proposal.
          </p>
          <div className="bg-accent/5 rounded-xl p-6 max-w-md mx-auto">
            <h4 className="font-headline font-semibold text-lg text-primary mb-3">
              What happens next?
            </h4>
            <ul className="font-body text-left text-text-secondary space-y-2">
              <li className="flex items-start gap-3">
                <Icon name="CheckIcon" size={20} variant="solid" className="text-accent flex-shrink-0 mt-0.5" />
                <span>Our sales engineer reviews your requirements</span>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="CheckIcon" size={20} variant="solid" className="text-accent flex-shrink-0 mt-0.5" />
                <span>We prepare a customized proposal with specifications</span>
              </li>
              <li className="flex items-start gap-3">
                <Icon name="CheckIcon" size={20} variant="solid" className="text-accent flex-shrink-0 mt-0.5" />
                <span>You receive a detailed quote within 12 hours</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 ${className}`}>
      <div className="text-center mb-8">
        <h3 className="font-headline font-bold text-3xl text-primary mb-3">
          Request a Quote
        </h3>
        <p className="font-body text-lg text-text-secondary">
          Fill out the form below and our team will respond within 12 hours
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <div>
          <label className="font-body text-sm font-medium text-text-primary mb-2 block">
            Name
          </label>
          <input
            type="text"
            name="contactName"
            value={formData.contactName}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full px-4 py-3 bg-surface border rounded-lg font-body text-base text-text-primary focus:outline-none focus:ring-2 transition-colors ${
              fieldErrors.contactName && touchedFields.has('contactName')
                ? 'border-error focus:ring-error' :'border-border focus:ring-accent'
            }`}
            placeholder="John Smith"
          />
          {fieldErrors.contactName && touchedFields.has('contactName') && (
            <div className="flex items-start gap-2 mt-2 animate-fadeIn">
              <Icon name="ExclamationCircleIcon" size={16} variant="solid" className="text-error flex-shrink-0 mt-0.5" />
              <p className="font-body text-sm text-error">{fieldErrors.contactName}</p>
            </div>
          )}
        </div>

        {/* Company Field */}
        <div>
          <label className="font-body text-sm font-medium text-text-primary mb-2 block">
            Company
          </label>
          <input
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full px-4 py-3 bg-surface border rounded-lg font-body text-base text-text-primary focus:outline-none focus:ring-2 transition-colors ${
              fieldErrors.companyName && touchedFields.has('companyName')
                ? 'border-error focus:ring-error' :'border-border focus:ring-accent'
            }`}
            placeholder="Your Company Ltd."
          />
          {fieldErrors.companyName && touchedFields.has('companyName') && (
            <div className="flex items-start gap-2 mt-2 animate-fadeIn">
              <Icon name="ExclamationCircleIcon" size={16} variant="solid" className="text-error flex-shrink-0 mt-0.5" />
              <p className="font-body text-sm text-error">{fieldErrors.companyName}</p>
            </div>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label className="font-body text-sm font-medium text-text-primary mb-2 block">
            Email *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full px-4 py-3 bg-surface border rounded-lg font-body text-base text-text-primary focus:outline-none focus:ring-2 transition-colors ${
              fieldErrors.email && touchedFields.has('email')
                ? 'border-error focus:ring-error' :'border-border focus:ring-accent'
            }`}
            placeholder="john@company.com"
          />
          {fieldErrors.email && touchedFields.has('email') && (
            <div className="flex items-start gap-2 mt-2 animate-fadeIn">
              <Icon name="ExclamationCircleIcon" size={16} variant="solid" className="text-error flex-shrink-0 mt-0.5" />
              <p className="font-body text-sm text-error">{fieldErrors.email}</p>
            </div>
          )}
          <p className="font-body text-xs text-text-secondary mt-1">We respect your privacy</p>
        </div>

        {/* WhatsApp Number Field */}
        <div>
          <label className="font-body text-sm font-medium text-text-primary mb-2 block">
            WhatsApp Number *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full px-4 py-3 bg-surface border rounded-lg font-body text-base text-text-primary focus:outline-none focus:ring-2 transition-colors ${
              fieldErrors.phone && touchedFields.has('phone')
                ? 'border-error focus:ring-error' :'border-border focus:ring-accent'
            }`}
            placeholder="+1 (555) 123-4567"
          />
          {fieldErrors.phone && touchedFields.has('phone') && (
            <div className="flex items-start gap-2 mt-2 animate-fadeIn">
              <Icon name="ExclamationCircleIcon" size={16} variant="solid" className="text-error flex-shrink-0 mt-0.5" />
              <p className="font-body text-sm text-error">{fieldErrors.phone}</p>
            </div>
          )}
          <p className="font-body text-xs text-text-secondary mt-1">Response within 2 hours</p>
        </div>

        {/* Description Field */}
        <div>
          <label className="font-body text-sm font-medium text-text-primary mb-2 block">
            Description *
          </label>
          <p className="font-body text-xs text-text-secondary mb-2">
            Please describe your project and indicate what purpose the LED walls will serve
          </p>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={5}
            className={`w-full px-4 py-3 bg-surface border rounded-lg font-body text-base text-text-primary focus:outline-none focus:ring-2 transition-colors resize-none ${
              fieldErrors.message && touchedFields.has('message')
                ? 'border-error focus:ring-error' :'border-border focus:ring-accent'
            }`}
            placeholder="Example: We need LED walls for our corporate conference room to display presentations and video conferences. The space is approximately 50 square meters with ambient lighting..."
          />
          {fieldErrors.message && touchedFields.has('message') && (
            <div className="flex items-start gap-2 mt-2 animate-fadeIn">
              <Icon name="ExclamationCircleIcon" size={16} variant="solid" className="text-error flex-shrink-0 mt-0.5" />
              <p className="font-body text-sm text-error">{fieldErrors.message}</p>
            </div>
          )}
        </div>

        {/* Submit Error */}
        {submitError && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 animate-fadeIn">
            <div className="flex items-start gap-3">
              <Icon name="ExclamationCircleIcon" size={20} variant="solid" className="text-error flex-shrink-0 mt-0.5" />
              <p className="font-body text-sm text-error">{submitError}</p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-accent hover:bg-accent-dark text-white font-headline font-semibold text-lg py-4 px-8 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
        >
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Sending Request...</span>
            </>
          ) : (
            <>
              <span>Get Your Quote</span>
              <Icon name="ArrowRightIcon" size={20} variant="solid" />
            </>
          )}
        </button>

        <p className="font-body text-xs text-center text-text-secondary">
          By submitting this form, you agree to our terms and conditions. We'll respond within 12 hours.
        </p>
      </form>
    </div>
  );
};

export default QuoteForm;